import React from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { truncateString } from "@qgiv/core-js";
import { Icon } from "@qgiv/core-react";
import { FormikField, TextButton } from "@qgiv/core-donor";
import { submitPromoCodes } from "@qgiv/p2p-registration";
import { useDispatch } from "react-redux";
import ApplyPromoButton from "./ApplyPromoButton";

// Helper function to determine the participants' categories
const getParticipantCategories = (registrations = []) => {
    const participantsCategory = registrations.map((participant) => {
        let categoryId;

        if (participant && participant.Category && participant.Category[0]) {
            // eslint-disable-next-line prefer-destructuring
            categoryId = participant.Category[0];
        }

        return { CategoryId: categoryId };
    });

    return participantsCategory;
};

const PromoChips = ({
    dispatch,
    formId,
    promoCodes,
    registrations,
    updatePromoCodes,
    updatePromoDiscounts,
    removeParticipantLinkLabel,
    promoCodeAppliedLabel,
}) => {
    const handleRemovePromo = (removedPromo) => {
        // Get category id for all registrants' categories
        const participantsCategory = getParticipantCategories(registrations);

        // Find and filter out of array item to be removed
        let promoArray = promoCodes ? [...promoCodes] : [];
        promoArray = promoArray && promoArray.filter((e) => e !== removedPromo);

        // 1. Assemble data for form submission
        const submitValues =
            promoArray.length > 0
                ? {
                      promoCodes: promoArray,
                      participants: participantsCategory,
                  }
                : {
                      promoCodes: promoArray,
                      participants: participantsCategory,
                      removeLastPromo: true,
                  };

        // make network call to update state
        submitPromoCodes(formId, submitValues)
            .then((response) => {
                const { data } = response;
                if (data) {
                    dispatch(updatePromoCodes(promoArray));
                    dispatch(updatePromoDiscounts(data.response));
                }
            })

            .catch((errors) => {
                // eslint-disable-next-line no-console
                console.error(errors);
            });
    };

    return (
        <div className="grid">
            {promoCodes.map((promo, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="col col--12 -type--small" key={i}>
                    <span className="registration-summary__callout -margin-top--5">
                        <Icon
                            className="-type--regular -font-weight--bold -margin-right--10"
                            glyph="check-solid"
                            type="FontAwesome"
                        />
                        <strong>{truncateString(promo, 20)}</strong>{" "}
                        {promoCodeAppliedLabel}
                    </span>
                    <TextButton
                        name="Remove_Promo"
                        type="submit"
                        handleClick={() => {
                            handleRemovePromo(promo);
                        }}
                        styles="text-input -margin-left--10"
                    >
                        <strong>{removeParticipantLinkLabel}</strong>
                    </TextButton>
                </div>
            ))}
        </div>
    );
};

PromoChips.propTypes = {
    dispatch: PropTypes.func.isRequired,
    formId: PropTypes.number.isRequired,
    promoCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
    registrations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    updatePromoCodes: PropTypes.func.isRequired,
    updatePromoDiscounts: PropTypes.func.isRequired,
    promoCodeAppliedLabel: PropTypes.string.isRequired,
    removeParticipantLinkLabel: PropTypes.string.isRequired,
};

const PromoCodes = ({
    isCms,
    formId,
    isVisible,
    promoCodes,
    promoDiscounts,
    registrations,
    updatePromoCodes,
    updatePromoDiscounts,
    removeParticipantLinkLabel,
    promoCodeLabel,
    promoCodeAppliedLabel,
    applyButtonLabel,
}) => {
    const dispatch = useDispatch();
    const hasAppliedPromos = promoCodes && promoCodes.length > 0;

    const onSubmit = (values, actions) => {
        const { setSubmitting, setValues, setFieldError } = actions;

        // Promo Codes are case insensitive, allows us to check for duplicates before saving
        const currentPromo = values.Promo_Code;

        // Add new input to existing array from state only if if doesn't already exist
        let promoArray = [];
        if (promoCodes.includes(currentPromo) || !currentPromo) {
            promoArray = [...promoCodes];
        } else {
            promoArray = [...promoCodes, currentPromo];
        }

        // 1. Assemble data for form submission
        const submitValues = {
            promoCodes: [...promoArray],
            participants: [...getParticipantCategories(registrations)],
        };

        submitPromoCodes(formId, submitValues)
            .then((response) => {
                const { data } = response;
                const {
                    response: {
                        ineligiblePromoCodes = [],
                        invalidPromoCodes = [],
                    },
                } = data;

                let errorMessages = [];
                let invalidPromo = false;
                let ineligiblePromo = false;

                //  Check if ineligible or invalid, if so pass status and errorMessage
                if (response && ineligiblePromoCodes.length > 0) {
                    errorMessages = ineligiblePromoCodes.find(
                        (_ineligiblePromo) =>
                            _ineligiblePromo.name.toLowerCase() ===
                            currentPromo.toLowerCase(),
                    ).errors;

                    if (errorMessages) {
                        ineligiblePromo = true;
                    }
                }

                // This handles No promo, inactive, deleted, or any other miscellaneous error
                //  or error that hasn’t been accounted for.
                // We do not want these store in redux
                if (
                    invalidPromoCodes &&
                    Object.keys(invalidPromoCodes).length > 0
                ) {
                    invalidPromo =
                        Object.values(invalidPromoCodes).includes(currentPromo);

                    if (invalidPromo || !currentPromo) {
                        errorMessages.push(
                            `"${currentPromo}" is not a valid promo code.`,
                        );
                    }
                }

                setSubmitting(false);
                if (data.response) {
                    if (!invalidPromo && !ineligiblePromo) {
                        dispatch(updatePromoDiscounts(data.response));
                        dispatch(updatePromoCodes(promoArray));

                        setValues({
                            Promo_Code: "",
                        });
                    } else {
                        // eslint-disable-next-line array-callback-return
                        errorMessages.map((errorText) => {
                            setFieldError("Promo_Code", errorText);
                        });
                    }
                }
            })
            .catch((errors) => {
                // eslint-disable-next-line no-console
                console.error(errors);
                setSubmitting(false);
            });
    };

    const formikProps = {
        initialValues: { Promo_Code: "" },
        onSubmit,
    };

    const promoProps = {
        dispatch,
        formId,
        promoCodes,
        promoDiscounts,
        registrations,
        updatePromoCodes,
        updatePromoDiscounts,
        removeParticipantLinkLabel,
        promoCodeAppliedLabel,
    };

    return (
        <Formik {...formikProps}>
            <Form>
                {isVisible && (
                    <>
                        <div className="grid">
                            <div className="col col--8">
                                <FormikField
                                    type="text"
                                    name="Promo_Code"
                                    id="Promo-Code"
                                    label={promoCodeLabel}
                                />
                            </div>
                            <div className="col col--4">
                                <ApplyPromoButton
                                    applyButtonLabel={applyButtonLabel}
                                />
                            </div>
                        </div>
                        {hasAppliedPromos &&
                            promoCodes.length > 0 &&
                            !isCms && <PromoChips {...promoProps} />}
                    </>
                )}
            </Form>
        </Formik>
    );
};

PromoCodes.propTypes = {
    isCms: PropTypes.bool.isRequired,
    isVisible: PropTypes.bool.isRequired,
    formId: PropTypes.number.isRequired,
    promoCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
    promoDiscounts: PropTypes.shape({}).isRequired,
    registrations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    updatePromoCodes: PropTypes.func.isRequired,
    updatePromoDiscounts: PropTypes.func.isRequired,
    promoCodeLabel: PropTypes.string.isRequired,
    promoCodeAppliedLabel: PropTypes.string.isRequired,
    applyButtonLabel: PropTypes.string.isRequired,
    removeParticipantLinkLabel: PropTypes.string.isRequired,
};

export default PromoCodes;
